/**
 * AdminScoringPage.js — /admin/scoring
 *
 * RC 1.47.ADMIN-SCORING-UI (Phase 1, 2026-05-02).
 *
 * Lightweight admin surface for the read-only
 * `GET /api/admin/scoring/rubric` endpoint shipped in
 * RC 1.46.RUBRIC-OBSERVABILITY. Renders:
 *
 *   - Rubric version, scoring-model version, model used
 *   - Current / stale / total row counts
 *   - Stale % rollout health gauge
 *   - Operator notes on what the numbers mean and how to act on them
 *
 * Hard rails:
 *   - Read-only. No mutating calls. No buttons that change state.
 *   - Uses the existing Card / Badge / Button conventions from
 *     `AdminBillingPage` so the operator surface stays cohesive.
 *   - Single endpoint round-trip on mount + on Refresh click.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ShieldCheck, AlertTriangle, GitBranch, Cpu,
} from 'lucide-react';

function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-GB').format(Number(n));
}

function HealthBadge({ stalePct }) {
  if (stalePct === null || stalePct === undefined) {
    return <Badge variant="outline" data-testid="scoring-health-unknown">Unknown</Badge>;
  }
  if (stalePct === 0) {
    return (
      <Badge
        className="bg-emerald-500/10 text-emerald-200 border-emerald-500/40"
        variant="outline"
        data-testid="scoring-health-fully-rolled"
      >
        Fully rolled out
      </Badge>
    );
  }
  if (stalePct < 25) {
    return (
      <Badge
        className="bg-emerald-500/10 text-emerald-200 border-emerald-500/40"
        variant="outline"
        data-testid="scoring-health-healthy"
      >
        Healthy rollout · {stalePct}% legacy
      </Badge>
    );
  }
  if (stalePct < 75) {
    return (
      <Badge
        className="bg-amber-500/10 text-amber-200 border-amber-500/40"
        variant="outline"
        data-testid="scoring-health-rolling"
      >
        Rolling out · {stalePct}% legacy
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-rose-500/10 text-rose-200 border-rose-500/40"
      variant="outline"
      data-testid="scoring-health-fresh-bump"
    >
      Recent bump · {stalePct}% legacy
    </Badge>
  );
}

export default function AdminScoringPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const d = await apiFetch('/api/admin/scoring/rubric');
      setData(d);
    } catch (err) {
      toast.error(err?.message || 'Failed to load scoring snapshot.');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ndor-ink-secondary py-12 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading scoring snapshot…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-scoring-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ndor-ink leading-tight">Scoring Governance</h1>
          <p className="text-sm text-ndor-ink-secondary leading-relaxed max-w-2xl">
            Live rubric, scoring-model, and rollout posture. After a rubric bump,
            cached rows auto-invalidate on the next analyse-fit per RC 1.46.REANALYSE-INTEGRITY —
            no manual sweep needed. Watch the stale % trend toward 0 as users open
            their jobs.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={refreshing}
          data-testid="admin-scoring-refresh"
        >
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Headline cards */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="border-ndor-edge">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
              <GitBranch className="h-3 w-3" /> Rubric version
            </CardDescription>
            <CardTitle className="text-2xl font-mono-ui tabular-nums" data-testid="admin-scoring-rubric-version">
              {data?.rubric_version || '—'}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-ndor-edge">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
              <ShieldCheck className="h-3 w-3" /> Scoring model
            </CardDescription>
            <CardTitle className="text-2xl font-mono-ui tabular-nums" data-testid="admin-scoring-model-version">
              {data?.scoring_model_version || '—'}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-ndor-edge">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
              <Cpu className="h-3 w-3" /> Model used for fit
            </CardDescription>
            <CardTitle className="text-base font-mono-ui truncate" data-testid="admin-scoring-model-used">
              {data?.model_used_for_fit || '—'}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Rollout posture */}
      <Card className="border-ndor-edge">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Rollout posture <HealthBadge stalePct={data?.stale_pct} />
          </CardTitle>
          <CardDescription className="text-xs leading-relaxed">
            Stale rows carry a previous rubric / model stamp and will auto-refresh
            on the next analyse-fit (cache miss → fresh LLM run → new stamp).
            No manual action required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Current" value={fmtNum(data?.current_row_count)} testId="admin-scoring-current" tone="ok" />
            <Stat label="Stale"   value={fmtNum(data?.stale_row_count)}   testId="admin-scoring-stale"   tone={data?.stale_row_count > 0 ? 'warn' : 'ok'} />
            <Stat label="Total"   value={fmtNum(data?.total_row_count)}   testId="admin-scoring-total" />
          </div>

          {/* Linear gauge — CSS bar, no chart lib needed */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px] font-mono-ui uppercase tracking-[0.14em] text-ndor-ink-muted">
              <span>Stamped on current</span>
              <span data-testid="admin-scoring-stale-pct">{data?.stale_pct ?? 0}% stale</span>
            </div>
            <div className="h-2 rounded-full bg-ndor-edge overflow-hidden">
              <div
                className="h-2 bg-emerald-500 transition-all"
                style={{ width: `${100 - (data?.stale_pct ?? 0)}%` }}
                data-testid="admin-scoring-rollout-bar"
              />
            </div>
          </div>

          {data?.stale_row_count > 0 ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 leading-relaxed">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <strong>{fmtNum(data.stale_row_count)} rows</strong> are still
                stamped with a previous rubric/model. They will auto-refresh
                organically as users analyse-fit on those jobs. To force a sweep,
                use the existing per-job <em>Re-analyse Fit</em> button (no admin
                tool yet, by design — preserves credit/cache integrity).
              </span>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200 leading-relaxed">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>Every <strong>job_scores</strong> row carries the live rubric and scoring-model stamps. Rollout integrity: 100%.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[10px] text-ndor-ink-muted font-mono-ui leading-relaxed">
        Source: <code>GET /api/admin/scoring/rubric</code> · RC 1.46.RUBRIC-OBSERVABILITY · read-only
      </p>
    </div>
  );
}

function Stat({ label, value, testId, tone }) {
  let toneClass = 'text-ndor-ink';
  if (tone === 'ok')   toneClass = 'text-emerald-200';
  if (tone === 'warn') toneClass = 'text-amber-200';
  return (
    <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">{label}</p>
      <p className={`text-xl font-mono-ui tabular-nums ${toneClass}`} data-testid={testId}>{value}</p>
    </div>
  );
}
